import { Box, Link, Skeleton, Typography } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { RecentSearchIcon } from "../../../../../assets/icons/MUI/LayoutIcons";
import { colors } from "../../../../../theme/palette";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getRecentSearch } from "../../../../../api/search";
import { UserInfo } from "../../../../../types";
import { useDataFormatter } from "../../../../../hooks/useDataFormatter";
import { useNavigate } from "react-router-dom";

const RecentSearchList = ({
  setIsFocused,
  clearSearch,
}: {
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  clearSearch: () => void;
}) => {
  const { t } = useTranslation();
  const { data: recentSearchData, isLoading: isRecentSearchDataLoading } = useQuery({
    queryKey: ["recentSearchData"],
    queryFn: getRecentSearch,
  });

  return (
    <>
      <Typography
        sx={{
          fontFamily: "Montserrat",
          fontSize: "12px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
          display: "block",
          marginBottom: "17px",
          color: colors.Dark1000,
        }}
      >
        {t("pages.search.recent")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        {!isRecentSearchDataLoading && recentSearchData?.length === 0 && (
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              display: "block",
              color: colors.Dark1000,
            }}
          >
            {t("pages.search.noRecentSearches")}
          </Typography>
        )}

        {!isRecentSearchDataLoading &&
          recentSearchData?.length > 0 &&
          recentSearchData.map((user: UserInfo, idx: number) => (
            <UserLink
              key={user.id}
              user={user}
              isLast={idx === recentSearchData?.length - 1}
              setIsFocused={setIsFocused}
              clearSearch={clearSearch}
            />
          ))}

        {isRecentSearchDataLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
            {[...Array(5)].map((_, idx) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: "16px",
                  paddingBottom: idx !== 4 ? "16px" : "",
                  borderBottom: idx !== 4 ? "1px solid #EAECF0" : "",
                }}
              >
                <RecentSearchIcon />
                <Skeleton
                  variant="rounded"
                  animation="wave"
                  sx={{
                    height: "22px",
                    width: "120px",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
};

export default RecentSearchList;

const UserLink = ({
  user,
  isLast,
  setIsFocused,
  clearSearch,
}: {
  user: UserInfo;
  isLast: boolean;
  setIsFocused: Dispatch<SetStateAction<boolean>>;
  clearSearch: () => void;
}) => {
  const { fullName, id, profileLink } = useDataFormatter(user);
  const navigate = useNavigate();
  const handleNavigate = () => {
    setIsFocused(false);
    clearSearch();
    navigate(profileLink);
  };
  return (
    <Box
      key={id}
      sx={{
        display: "flex",
        alignItems: "center",
        columnGap: "16px",
        paddingBottom: !isLast ? "16px" : "",
        borderBottom: !isLast ? "1px solid #EAECF0" : "",
      }}
    >
      <RecentSearchIcon />
      <Link
        onClick={handleNavigate}
        sx={{
          color: colors.Dark1000,
          fontFamily: "Montserrat",
          fontSize: "14px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "24px",
          textDecoration: "none",
          cursor: "pointer",
        }}
      >
        {fullName}
      </Link>
    </Box>
  );
};
